/* Desktop header navigation, adapted from Migration2Horizon interaction timings. */
(() => {
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 150;
  const initialized = new WeakSet();

  function initialize(header) {
    if (!header || initialized.has(header)) return;
    const items = [...header.querySelectorAll('[data-fp-menu]')].filter(
      (item) => item.querySelector('[data-fp-menu-trigger]') && item.querySelector('[data-fp-menu-panel]')
    );
    if (!items.length && !header.querySelector('[data-fp-secondary-row]')) return;
    initialized.add(header);
    const controller = new AbortController();
    const on = (target, name, listener, options = {}) =>
      target.addEventListener(name, listener, { signal: controller.signal, ...options });

    let active = null;
    let openTimer = null;
    let closeTimer = null;
    let touchIntent = null;
    let pointerIntent = null;
    const section = header.closest('.section-header');
    const secondaryRow = header.querySelector('[data-fp-secondary-row]');
    const desktop = window.matchMedia('(min-width: 990px)');
    const touchCapable = window.matchMedia('(any-pointer: coarse)');
    let secondaryCloseTimer = null;
    let secondaryFrame = null;
    let dismissedByEscape = false;
    let suppressFocusOpen = false;
    let pointerInside = false;
    let lastCompact = null;
    let lastExpanded = null;

    const syncSecondaryRow = () => {
      if (!section || !secondaryRow) return;
      // Keep category links visible when the device has no reliable hover path.
      const compact = desktop.matches && !touchCapable.matches && window.scrollY > 48;
      const interacting = pointerInside || header.contains(document.activeElement) || Boolean(active);
      const expanded = compact && interacting && !dismissedByEscape;
      if (compact === lastCompact && expanded === lastExpanded) return;
      lastCompact = compact;
      lastExpanded = expanded;
      section.toggleAttribute('data-fp-compact', compact);
      section.toggleAttribute('data-fp-secondary-open', expanded);
      secondaryRow.inert = compact && !expanded;
      if (compact && !expanded) secondaryRow.setAttribute('aria-hidden', 'true');
      else secondaryRow.removeAttribute('aria-hidden');
    };
    const queueSecondarySync = () => {
      if (secondaryFrame) return;
      secondaryFrame = requestAnimationFrame(() => {
        secondaryFrame = null;
        syncSecondaryRow();
      });
    };

    const cancelOpen = () => {
      clearTimeout(openTimer);
      openTimer = null;
    };
    const cancelClose = () => {
      clearTimeout(closeTimer);
      closeTimer = null;
    };
    const setOpen = (item, value) => {
      item.toggleAttribute('data-fp-menu-open', value);
      item.querySelector('[data-fp-menu-trigger]').setAttribute('aria-expanded', String(value));
      if (value) {
        item.querySelectorAll('[data-fp-menu-panel] img[data-fp-deferred-image]').forEach((image) => {
          image.loading = 'eager';
          if (image.dataset.srcset) image.srcset = image.dataset.srcset;
          if (image.dataset.src) image.src = image.dataset.src;
          image.removeAttribute('data-fp-deferred-image');
          image.removeAttribute('data-srcset');
          image.removeAttribute('data-src');
        });
      }
    };
    const close = () => {
      cancelOpen();
      cancelClose();
      if (active) setOpen(active, false);
      active = null;
      syncSecondaryRow();
    };
    const open = (item) => {
      cancelOpen();
      cancelClose();
      if (active === item) return;
      if (active) setOpen(active, false);
      active = item;
      setOpen(item, true);
      syncSecondaryRow();
    };
    const scheduleOpen = (item) => {
      cancelOpen();
      cancelClose();
      if (active) open(item);
      else openTimer = setTimeout(() => open(item), OPEN_DELAY);
    };
    const scheduleClose = () => {
      cancelOpen();
      cancelClose();
      closeTimer = setTimeout(() => {
        if (!active?.contains(document.activeElement)) close();
      }, CLOSE_DELAY);
    };

    for (const item of items) {
      const trigger = item.querySelector('[data-fp-menu-trigger]');
      on(item, 'pointerenter', (event) => {
        if (event.pointerType === 'mouse' || event.pointerType === 'pen') scheduleOpen(item);
      });
      on(item, 'pointerleave', (event) => {
        if (event.pointerType === 'mouse' || event.pointerType === 'pen') scheduleClose();
      });
      on(item, 'focusin', () => {
        if (touchIntent?.item !== item) open(item);
      });
      on(trigger, 'pointerdown', (event) => {
        pointerIntent = { item, wasOpen: active === item };
        touchIntent = event.pointerType === 'touch' ? { item, wasOpen: active === item } : null;
      });
      on(trigger, 'click', (event) => {
        if (trigger.tagName === 'BUTTON') {
          event.preventDefault();
          const wasOpen = pointerIntent?.item === item ? pointerIntent.wasOpen : active === item;
          wasOpen ? close() : open(item);
          pointerIntent = null;
          touchIntent = null;
          return;
        }
        // First touch exposes the submenu; a second tap follows the parent link.
        if (touchIntent?.item === item && !touchIntent.wasOpen) {
          event.preventDefault();
          open(item);
        }
        pointerIntent = null;
        touchIntent = null;
      });
    }

    on(header, 'pointerenter', (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      clearTimeout(secondaryCloseTimer);
      pointerInside = true;
      dismissedByEscape = false;
      syncSecondaryRow();
    });
    on(header, 'pointerleave', (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      clearTimeout(secondaryCloseTimer);
      secondaryCloseTimer = setTimeout(() => {
        secondaryCloseTimer = null;
        pointerInside = false;
        syncSecondaryRow();
      }, 120);
    });
    on(header, 'focusin', () => {
      if (!suppressFocusOpen) dismissedByEscape = false;
      syncSecondaryRow();
    });
    on(header, 'keydown', (event) => {
      if (event.key !== 'Escape' || !section?.hasAttribute('data-fp-compact') || !section.hasAttribute('data-fp-secondary-open')) return;
      if (event.target.closest?.('predictive-search, details-modal, .search-modal, [role="dialog"]')) return;
      event.preventDefault();
      event.stopPropagation();
      dismissedByEscape = true;
      suppressFocusOpen = true;
      header.querySelector('.header__heading-link')?.focus();
      suppressFocusOpen = false;
      close();
    });
    on(window, 'scroll', queueSecondarySync, { passive: true });
    on(window, 'resize', () => {
      if (!desktop.matches || touchCapable.matches) {
        pointerInside = false;
        dismissedByEscape = false;
        close();
      } else {
        syncSecondaryRow();
      }
    });

    on(header, 'focusout', (event) => {
      if (!header.contains(event.relatedTarget)) {
        dismissedByEscape = false;
        close();
        queueMicrotask(syncSecondaryRow);
      }
    });
    on(document, 'keydown', (event) => {
      if (event.key !== 'Escape' || !active) return;
      event.preventDefault();
      const trigger = active.querySelector('[data-fp-menu-trigger]');
      trigger.focus();
      close();
    });
    on(document, 'pointerdown', (event) => {
      if (!header.contains(event.target)) close();
    });
    on(document, 'shopify:section:unload', (event) => {
      if (!event.target.contains(header)) return;
      close();
      clearTimeout(secondaryCloseTimer);
      if (secondaryFrame) cancelAnimationFrame(secondaryFrame);
      controller.abort();
      initialized.delete(header);
    });
    header.classList.add('fp-header-nav-ready');
    syncSecondaryRow();
  }

  const init = (root = document) => root.querySelectorAll('.section-header .header').forEach(initialize);
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
